import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import copy from 'copy-to-clipboard';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Contact, Zap, Activity } from 'lucide-react';
import { Tooltip } from '@getro/rombo';
import { IntegrationStatus } from 'components/atoms/IntegrationStatus';
import { trackEvent } from 'helpers/analytics';
import IntegrationIcon from './IntegrationIcon';
import { getWarningClipboardText, getInviteClipboardText } from '../helpers/clipboardMessages';
import { CopyLink } from '../helpers/copyLink';

const INTEGRATION_LABELS = { linkedin: 'LinkedIn', google: 'Google', outlook: 'Outlook' };

const ADAPTER_TO_PILL_STATUS = {
  not_connected: 'not_completed',
  action_needed: 'reconnection_needed',
};

// Keys mirror the backend `google_features` payload (manager/ally serializers).
const FEATURE_LABELS = [
  { key: 'relationship_strength', label: 'Relationship strength' },
  { key: 'contacts', label: 'Contacts' },
];

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 32px;
`;

const Detail = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.main};
`;

// Matches Lucide icon width (16px) so feature rows line up with sync/contact rows above them.
const FeatureIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  font-size: 14px;
  color: ${({ theme, $enabled }) => ($enabled ? theme.colors.green[600] : theme.colors.text.subtle)};
`;

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ theme, $tone }) => ($tone === 'info' ? theme.colors.neutral[50] : theme.colors.orange[50])};
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.main};
`;

const ExternalLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

// Exact elapsed time so the label stays honest and lines up with the 30-day
// sync-dot threshold, instead of moment's fuzzy buckets ("a month ago" at 26
// days, "a day ago" at 22 hours). Whole days once a gap crosses a day, then
// exact hours/minutes below that ("Extension seen" is often minutes old).
const fromNowOrDash = (value) => {
  if (!value) return '—';
  const then = moment(value);
  const now = moment();
  // These are past events (last sync / last seen); a future value only comes from
  // client/server clock skew, so read it as "just now" rather than moment's
  // misleading "in 5 minutes".
  if (then.isAfter(now)) return 'just now';
  const days = now.diff(then, 'days');
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'} ago`;
  const hours = now.diff(then, 'hours');
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const minutes = now.diff(then, 'minutes');
  if (minutes >= 1) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  return 'just now';
};

const renderLinkedinWarning = (rawStatus, name) => {
  switch (rawStatus) {
    case 'reconnection_needed':
      return `Sync activity has stalled for ${name}. Verifying their setup will start it again.`;
    case 'no_recent_sync':
      return `The Getro extension hasn't checked in for ${name} recently. They'll need to confirm it's running in their usual browser.`;
    case 'not_logged_in_to_linkedin':
      return (
        <>
          We&apos;re not seeing an active LinkedIn session for {name}. They&apos;ll need to sign in to{' '}
          <ExternalLink href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer">
            LinkedIn
          </ExternalLink>{' '}
          or verify their setup.
        </>
      );
    default:
      return null;
  }
};

const IntegrationPopoverRow = ({ integration, memberFirstName, networkSlug, isCurrentUser }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const integrationType = INTEGRATION_LABELS[integration.type];
  const nameForCopy = memberFirstName || 'them';

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = useCallback((text, { eventName, attrs } = {}) => {
    copy(text);
    if (eventName) trackEvent(eventName, attrs);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  }, []);

  const isLinkedin = integration.type === 'linkedin';
  // Paused beats stale (mirrors the dot) so pill and dot never disagree.
  const isStale = isLinkedin && integration.health === 'stale' && integration.rawStatus !== 'paused';
  const healthKnown = integration.health != null;

  // Stale (>30d) reads as "Action needed" even if raw status is still "active".
  const pillStatus = isStale
    ? 'reconnection_needed'
    : integration.rawStatus || ADAPTER_TO_PILL_STATUS[integration.status] || integration.status;

  // Calm tone only when health is known non-stale; absent health falls back to the
  // alarming tone so the pill matches the dot.
  const pillTone = isLinkedin && healthKnown && !isStale ? 'info' : 'warning';

  const renderCopyLink = (label, text, tracking) => {
    if (copied) {
      return <Tooltip content="Copied!" isOpen delayHide={1000} trigger={<CopyLink>{label}</CopyLink>} />;
    }
    return <CopyLink onClick={() => handleCopy(text, tracking)}>{label}</CopyLink>;
  };

  const renderInviteDetail = () => {
    if (isCurrentUser) {
      return (
        <Detail>
          Connect your {integrationType} from the{' '}
          <Link to={`/networks/${networkSlug}/settings/integrations`}>Personal integrations screen</Link>.
        </Detail>
      );
    }

    const inviteText = getInviteClipboardText({ firstName: nameForCopy, networkSlug, integrationType });
    return (
      <Detail>
        Invite {nameForCopy} to connect their {integrationType}.{' '}
        {renderCopyLink('Copy message', inviteText, {
          eventName: 'team_members:copy_integration_invite_message',
          attrs: { integration: integration.type },
        })}
      </Detail>
    );
  };

  const renderIconRow = (Icon, content) => (
    <DetailRow>
      <Icon size={16} aria-hidden="true" />
      <span>{content}</span>
    </DetailRow>
  );

  const renderFeaturesList = (features) => {
    const enabled = new Set(features || []);
    return FEATURE_LABELS.map(({ key, label }) => {
      const isEnabled = enabled.has(key);
      return (
        <DetailRow key={key}>
          <FeatureIndicator $enabled={isEnabled} aria-label={isEnabled ? 'enabled' : 'not enabled'}>
            {isEnabled ? '✓' : '—'}
          </FeatureIndicator>
          <span>{label}</span>
        </DetailRow>
      );
    });
  };

  const renderLinkedinDetails = () => {
    const { rawStatus } = integration;

    if (integration.status === 'not_connected') return renderInviteDetail();

    if (rawStatus === 'syncing') {
      return (
        <>
          {renderIconRow(Zap, 'Sync in progress')}
          {renderIconRow(Activity, `Extension seen ${fromNowOrDash(integration.extensionLastOnlineAt)}`)}
        </>
      );
    }

    const showDetails = ['active', 'paused', 'action_needed'].includes(integration.status);
    if (!showDetails) return null;

    // Stale syncs always get an explanation, even when raw status has no warning.
    const transientWarning = renderLinkedinWarning(rawStatus, nameForCopy);
    const warningContent =
      transientWarning ||
      (isStale ? `LinkedIn hasn't synced for ${nameForCopy} in over 30 days. Reconnecting will start it again.` : null);
    const warningStatus = transientWarning ? rawStatus : 'stale';
    const clipboardText = warningContent
      ? getWarningClipboardText(warningStatus, { firstName: nameForCopy, networkSlug, integrationType })
      : null;

    return (
      <>
        {renderIconRow(Contact, `${integration.contactsSynced ?? 0} contacts synced`)}
        {renderIconRow(Zap, `Last sync ${fromNowOrDash(integration.lastSyncedAt)}`)}
        {renderIconRow(Activity, `Extension seen ${fromNowOrDash(integration.extensionLastOnlineAt)}`)}
        {warningContent && (
          <WarningBox $tone={pillTone}>
            <span>
              {warningContent}{' '}
              {isCurrentUser ? (
                <Link to={`/networks/${networkSlug}/settings/integrations`}>
                  Fix it from your Personal integrations screen
                </Link>
              ) : (
                renderCopyLink('Copy instructions', clipboardText, {
                  eventName: 'team_members:copy_fix_integration_message',
                  attrs: { integration: integration.type, status: warningStatus },
                })
              )}
            </span>
          </WarningBox>
        )}
      </>
    );
  };

  const renderGoogleOutlookDetails = () => {
    if (integration.status === 'not_connected') return renderInviteDetail();

    if (integration.status === 'syncing') {
      return (
        <>
          {renderFeaturesList(integration.features)}
          {renderIconRow(Zap, 'Sync in progress')}
        </>
      );
    }

    const showDetails = ['active', 'in_sync', 'permissions_missing'].includes(integration.status);
    if (!showDetails) return null;

    const clipboardText =
      integration.status === 'permissions_missing'
        ? getWarningClipboardText('permissions_missing', { firstName: nameForCopy, networkSlug, integrationType })
        : null;

    return (
      <>
        {renderFeaturesList(integration.features)}
        {renderIconRow(Contact, `${integration.contactsSynced ?? 0} contacts synced`)}
        {renderIconRow(Zap, `Last sync ${fromNowOrDash(integration.lastSyncedAt)}`)}
        {integration.status === 'permissions_missing' && (
          <WarningBox>
            <span>
              Permissions missing — some features aren&apos;t working.{' '}
              {isCurrentUser ? (
                <Link to={`/networks/${networkSlug}/settings/integrations`}>
                  Fix it from your Personal integrations screen
                </Link>
              ) : (
                renderCopyLink('Copy instructions', clipboardText, {
                  eventName: 'team_members:copy_fix_integration_message',
                  attrs: { integration: integration.type, status: 'permissions_missing' },
                })
              )}
            </span>
          </WarningBox>
        )}
      </>
    );
  };

  const details = integration.type === 'linkedin' ? renderLinkedinDetails() : renderGoogleOutlookDetails();

  return (
    <Row>
      <Header>
        <HeaderLeft>
          <IntegrationIcon type={integration.type} state={integration.iconState} />
          <Name>{integrationType}</Name>
        </HeaderLeft>
        <IntegrationStatus status={pillStatus} tone={pillTone} />
      </Header>
      {details && <Details>{details}</Details>}
    </Row>
  );
};

IntegrationPopoverRow.propTypes = {
  integration: PropTypes.shape({
    type: PropTypes.oneOf(['linkedin', 'google', 'outlook']).isRequired,
    status: PropTypes.string.isRequired,
    iconState: PropTypes.string.isRequired,
    health: PropTypes.oneOf(['never', 'paused', 'stale', 'healthy', null]),
    contactsSynced: PropTypes.number,
    lastSyncedAt: PropTypes.string,
    extensionLastOnlineAt: PropTypes.string,
    linkedinSessionPresent: PropTypes.bool,
    rawStatus: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  memberFirstName: PropTypes.string.isRequired,
  networkSlug: PropTypes.string.isRequired,
  isCurrentUser: PropTypes.bool.isRequired,
};

export default IntegrationPopoverRow;
